import type { Session as AnalyticsSession } from "@threekit/analytics";
import { OptionInteractionType } from "@threekit/rest-api";
import {
  buildOptionsShowForAttribute,
  getConfiguratorAttributeList,
  getCpqAttributeAltKeyFromDisplayAttribute,
  getCpqAttributeKeyFromDisplayAttribute,
  getLeftRightMultiSide,
  isAssetValue,
  isItemLikeAttributeType,
  isRecord,
  normalizeLeftRightSide,
  type LeftRightSide,
  type OptionsShowParams,
  valueToValueOption,
} from "./configurator-attribute-utils";

type OptionInteractionParams = Parameters<
  AnalyticsSession["optionInteraction"]
>[0];

/** Subset of `ThreekitEmbedHandle` needed for CPQ apply (avoids circular imports). */
export interface ApplyCPQSelectionsHandle {
  configurator: unknown;
  setConfiguration(configuration: unknown): Promise<unknown>;
  optionsShow(params: OptionsShowParams): void;
  optionInteraction(params: OptionInteractionParams): void;
  productAssetId?: string;
}

export interface ApplyCPQSelectionsResult {
  applied: string[];
  skipped: { cpqKey: string; reason: string }[];
}

export interface ApplyCPQSelectionsOptions {
  useDisplayAttributes?: boolean;
  /** @default true */
  emitOptionsShowBeforeInteraction?: boolean;
  /** When true, logs resolution, configuration, and analytics steps to the console. */
  debug?: boolean;
  /**
   * Called when internal async work finishes. CPQ can use this instead of Promises.
   */
  onComplete?: (error: Error | null, result: ApplyCPQSelectionsResult | null) => void;
  /**
   * Reads the live value of an arbitrary Oracle CPQ attribute by name — not limited to keys
   * present in the `selections` map passed to this call. Needed for `LeftRightMulti` attributes'
   * `metadata.leftRightQtyAttr`, which names a CPQ qty field that isn't necessarily linked to any
   * Threekit attribute (so it never appears in `selections` on its own). `CPQJS.getAttributeVal`
   * satisfies this signature directly; `registerCpqLinkedAttributeListeners` wires it in
   * automatically for direct embed unless the caller already supplied one.
   */
  getCpqAttributeValue?: (cpqAttribute: string) => unknown;
}

const LOG_PREFIX = "[applyCPQSelections]";

function dbg(opts: ApplyCPQSelectionsOptions, ...args: unknown[]): void {
  if (opts.debug) console.log(LOG_PREFIX, ...args);
}

function normalizeCpqKey(key: string): string {
  return key.trim();
}

function getMetadata(record: Record<string, unknown>): Record<string, unknown> | undefined {
  const m = record.metadata;
  return isRecord(m) ? m : undefined;
}

/** Threekit booleans are sometimes modeled as real booleans, sometimes as "TRUE"/"FALSE" strings. */
function isMetadataFlagTrue(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toUpperCase() === "TRUE";
  return false;
}

/**
 * Removes every occurrence of the literal word "Left"/"Right" from `value`, wherever it falls —
 * as a trailing " Left" suffix ("Unilink Flip Down Mounting Hardware Left"), or spliced into the
 * middle with no surrounding whitespace ("...AdjustableFlip DownLeftw/Full Surface Contact
 * Feature" → "...AdjustableFlip Downw/Full Surface Contact Feature"). Then collapses any
 * resulting double space and trims.
 */
function stripLeftRight(value: string): string {
  return value
    .replace(/Left|Right/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Oracle CPQ models some Threekit single-select attributes (e.g. "Pelvic/Thigh Lateral
 * Brackets") as a multi-select of "<option> Left" / "<option> Right" so the user can pick
 * either or both sides. Threekit only has the combined option. When the Threekit attribute's
 * `metadata.removeLeftRight` is set, strip "Left"/"Right" from each comma-separated CPQ value
 * and collapse to the single shared base value before matching `cpq_value`.
 */
function normalizeRemoveLeftRightValue(raw: string): string {
  const bases = raw
    .split(",")
    .map((part) => stripLeftRight(part))
    .filter((part) => part.length > 0);
  return bases[0] ?? raw.trim();
}

/** Splits a "~"-delimited multi-select CPQ value into its parts (trimmed, empties dropped). */
function splitTildeDelimitedValues(value: string): string[] {
  return value
    .split("~")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Splits a comma-delimited multi-select CPQ value into its parts (trimmed, empties dropped).
 * Used for `LeftRightMulti` attributes (see below) — same delimiter convention as
 * `normalizeRemoveLeftRightValue` above, not the "~" used by `takeFirstTildeDelimitedValue`.
 */
function splitCommaDelimitedValues(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Some CPQ fields are multi-select in CPQ but map to a single-select attribute in Threekit.
 * CPQ sends these as a "~"-delimited string (e.g. "Option A~Option B"). Since Threekit can only
 * hold one value, we take just the first delimited value and drop the rest.
 *
 * Not used for `LeftRightMulti` attributes (see below) — those need every selected value, not
 * just the first, so {@link runApplyCPQSelections} skips this step for them.
 */
function takeFirstTildeDelimitedValue(
  value: string | number,
  opts: ApplyCPQSelectionsOptions
): string | number {
  if (typeof value !== "string" || !value.includes("~")) return value;

  const parts = splitTildeDelimitedValues(value);
  const first = parts[0] ?? value.trim();

  dbg(opts, "takeFirstTildeDelimitedValue", { raw: value, parts, first });

  return first;
}

/** Match Oracle CPQ string/number to catalog metadata or raw values. */
export function cpqValuesMatch(cpqVal: string | number, catalogSide: unknown): boolean {
  if (catalogSide === undefined || catalogSide === null) return false;

  if (typeof cpqVal === "number" && Number.isFinite(cpqVal)) {
    if (typeof catalogSide === "number" && Number.isFinite(catalogSide)) {
      return cpqVal === catalogSide;
    }
    if (typeof catalogSide === "string" && catalogSide.trim() !== "") {
      const n = Number(catalogSide);
      if (Number.isFinite(n) && n === cpqVal) return true;
    }
  }

  const s1 = String(cpqVal).trim();
  const s2 = String(catalogSide).trim();
  return s1 === s2;
}

/**
 * Oracle CPQ sometimes models a multi-select field as one Threekit Boolean attribute per
 * possible value, each tagged on the attribute itself (not on its options) with
 * `metadata.multiselect: true` and `metadata.cpq_value: "<value>"`. When the linked
 * `cpq_attribute` changes, each such Boolean attribute is set to `true` if its `cpq_value` is
 * one of the comma-separated selections in the raw CPQ value, `false` otherwise.
 *
 * Returns `null` when `attr` isn't tagged this way, so the caller falls through to the normal
 * option-matching path in {@link findMatchingOption}.
 */
function resolveMultiselectBooleanValue(
  attr: Record<string, unknown>,
  cpqValue: string | number,
  opts: ApplyCPQSelectionsOptions
): boolean | null {
  const attrMeta = getMetadata(attr);
  if (!attrMeta) return null;
  if (!isMetadataFlagTrue(attrMeta.multiselect)) return null;

  const cpqValueMeta = attrMeta.cpq_value;
  const hasCpqValueMeta =
    (typeof cpqValueMeta === "string" || typeof cpqValueMeta === "number") &&
    String(cpqValueMeta).trim() !== "";
  if (!hasCpqValueMeta) return null;

  const selections = String(cpqValue)
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const isSelected = selections.some((s) => cpqValuesMatch(s, cpqValueMeta));

  dbg(opts, "resolveMultiselectBooleanValue", {
    attrName: typeof attr.name === "string" ? attr.name : "?",
    "metadata.cpq_value": cpqValueMeta,
    cpqValue,
    selections,
    isSelected,
  });

  return isSelected;
}

/**
 * Oracle CPQ sometimes maps a plain scalar field (e.g. a "Yes"/"No" dropdown) directly onto a
 * Threekit `Boolean` attribute. Booleans have no `values` array to hang `metadata.cpq_value`
 * off of, so instead the attribute itself carries `metadata.cpq_true_value`: whatever string
 * (or number) CPQ sends for the "true" case. When the linked `cpq_attribute` changes, the
 * attribute is set to `true` if the raw CPQ value matches `cpq_true_value`, `false` otherwise
 * (including when the CPQ value is empty/unset).
 *
 * Returns `null` when `attr` isn't tagged this way, so the caller falls through to the normal
 * option-matching path in {@link findMatchingOption}.
 */
function resolveScalarBooleanValue(
  attr: Record<string, unknown>,
  cpqValue: string | number,
  opts: ApplyCPQSelectionsOptions
): boolean | null {
  const attrMeta = getMetadata(attr);
  if (!attrMeta) return null;

  const trueValueMeta = attrMeta.cpq_true_value;
  const hasTrueValueMeta =
    (typeof trueValueMeta === "string" || typeof trueValueMeta === "number") &&
    String(trueValueMeta).trim() !== "";
  if (!hasTrueValueMeta) return null;

  const isTrue = cpqValuesMatch(cpqValue, trueValueMeta);

  dbg(opts, "resolveScalarBooleanValue", {
    attrName: typeof attr.name === "string" ? attr.name : "?",
    "metadata.cpq_true_value": trueValueMeta,
    cpqValue,
    isTrue,
  });

  return isTrue;
}

/**
 * Reads a value/option's `metadata.cpq_leftright` ("Left"/"Right") — see
 * {@link findMatchingLeftRightMultiOption}.
 */
function getOptionCpqLeftRight(meta: Record<string, unknown> | undefined): LeftRightSide | null {
  if (!meta) return null;
  return normalizeLeftRightSide(meta.cpq_leftright);
}

/**
 * Reads a value/option's `metadata.leftRightQtyAttr` — the Oracle CPQ attribute name that holds
 * the quantity for this (side-symmetric) option. Not necessarily itself linked to any Threekit
 * attribute via `cpq_attribute`, so its value has to be read live rather than found in the
 * `selections` map that triggered this apply — see {@link lookupCpqAttributeValue}.
 */
function getOptionLeftRightQtyAttr(meta: Record<string, unknown> | undefined): string | undefined {
  if (!meta) return undefined;
  const v = meta.leftRightQtyAttr;
  if (typeof v !== "string" && typeof v !== "number") return undefined;
  const s = String(v).trim();
  return s.length > 0 ? s : undefined;
}

/**
 * Resolves the live value of an arbitrary Oracle CPQ attribute (e.g. a `leftRightQtyAttr` qty
 * field) that may not be present in the `selections` map passed to `runApplyCPQSelections` —
 * that map only carries whichever field(s) actually changed. Prefers
 * `options.getCpqAttributeValue` (wired to `CPQJS.getAttributeVal` for direct embed by
 * {@link registerCpqLinkedAttributeListeners}); falls back to `selections`, in case the caller
 * already included the field there (e.g. a bulk sync via `buildCpqSelectionsForApply` that also
 * happened to read this key).
 */
function lookupCpqAttributeValue(
  cpqAttribute: string,
  selections: Record<string, string | number>,
  opts: ApplyCPQSelectionsOptions
): string | number | undefined {
  if (typeof opts.getCpqAttributeValue === "function") {
    const v = opts.getCpqAttributeValue(cpqAttribute);
    if (v !== undefined && v !== null) {
      return typeof v === "number" ? v : String(v).trim();
    }
  }
  return selections[normalizeCpqKey(cpqAttribute)];
}

/**
 * `metadata.leftRightQtyAttr` semantics: qty `1` → Left only, qty `2` → both sides. Any other
 * value (missing, blank, not 1 or 2) is treated as "not eligible for either side" rather than
 * guessed, and is logged for visibility.
 */
function qtyAllowsSide(
  qty: string | number | undefined,
  side: LeftRightSide,
  opts: ApplyCPQSelectionsOptions
): boolean {
  const n = typeof qty === "number" ? qty : typeof qty === "string" ? Number(qty.trim()) : NaN;
  if (n === 1) return side === "Left";
  if (n === 2) return true;
  dbg(opts, "qtyAllowsSide: qty is not 1 or 2 — not eligible for either side", { qty, side });
  return false;
}

/**
 * Searches a `LeftRightMulti` attribute's option list for one eligible for `side`, given every
 * currently-selected CPQ value (the multi-select field's comma-delimited parts — see
 * {@link runApplyCPQSelections}, which skips `takeFirstTildeDelimitedValue` for these attributes
 * so all selected values survive, not just the first, and splits on comma via
 * `splitCommaDelimitedValues` instead of "~").
 *
 * An option is a candidate at all only if its `metadata.cpq_value` matches one of the selected
 * values; among candidates, eligibility for `side` then depends on which flavor of metadata it
 * carries:
 *  - `metadata.cpq_leftright` ("Left"/"Right"): a side-specific option — eligible only when it
 *    names this `side`. Both a Left- and a Right-flagged value can be selected in CPQ at once.
 *  - `metadata.leftRightQtyAttr` (a CPQ qty field name): a side-symmetric option — eligibility
 *    comes from that field's live value via {@link qtyAllowsSide}.
 *  - Neither (e.g. the `CPQ_VALUE_FALLBACK` "None" catch-all): always eligible.
 *
 * Returns the first eligible match, mirroring {@link findMatchingOption}'s first-match-wins order.
 */
function findMatchingLeftRightMultiOption(
  attr: Record<string, unknown>,
  side: LeftRightSide,
  selectedValues: (string | number)[],
  selections: Record<string, string | number>,
  opts: ApplyCPQSelectionsOptions
): { setValue: unknown; optionId: string } | null {
  const attrName = typeof attr.name === "string" ? attr.name : "?";
  const typeStr = typeof attr.type === "string" ? attr.type : "";
  const rawValues = attr.values;
  if (!Array.isArray(rawValues)) {
    dbg(opts, "findMatchingLeftRightMultiOption: attr.values is not an array", {
      attrName,
      values: attr.values,
    });
    return null;
  }

  dbg(opts, "findMatchingLeftRightMultiOption: start", { attrName, side, selectedValues });

  const isItemLike = isItemLikeAttributeType(typeStr);

  for (const v of rawValues) {
    const isAsset = isAssetValue(v);
    if (isItemLike && !isAsset) {
      dbg(opts, "findMatchingLeftRightMultiOption: option is not an asset value, skipping", {
        attrName,
        option: v,
      });
      continue;
    }
    if (!isAsset && !isRecord(v)) continue;

    const meta = getMetadata(v as Record<string, unknown>);
    const cpqValueMeta = meta?.cpq_value;
    const hasCpqValue = !!meta && "cpq_value" in meta;
    if (!hasCpqValue) continue;

    const isSelected = selectedValues.some((sel) => cpqValuesMatch(sel, cpqValueMeta));
    if (!isSelected) continue;

    const leftRight = getOptionCpqLeftRight(meta);
    const qtyAttr = getOptionLeftRightQtyAttr(meta);

    let eligible: boolean;
    if (leftRight) {
      eligible = leftRight === side;
      dbg(opts, "findMatchingLeftRightMultiOption: cpq_leftright option", {
        attrName,
        option: describeOption(v),
        "metadata.cpq_leftright": leftRight,
        side,
        eligible,
      });
    } else if (qtyAttr) {
      const qty = lookupCpqAttributeValue(qtyAttr, selections, opts);
      eligible = qtyAllowsSide(qty, side, opts);
      dbg(opts, "findMatchingLeftRightMultiOption: leftRightQtyAttr option", {
        attrName,
        option: describeOption(v),
        "metadata.leftRightQtyAttr": qtyAttr,
        qty,
        side,
        eligible,
      });
    } else {
      eligible = true;
      dbg(opts, "findMatchingLeftRightMultiOption: plain shared option (no side metadata)", {
        attrName,
        option: describeOption(v),
        side,
        eligible,
      });
    }

    if (!eligible) continue;

    dbg(opts, "findMatchingLeftRightMultiOption: matched", { attrName, side, option: describeOption(v) });

    if (isAsset) {
      return { setValue: { assetId: v.assetId }, optionId: v.assetId };
    }
    if ("value" in (v as Record<string, unknown>)) {
      const val = (v as Record<string, unknown>).value;
      const opt = valueToValueOption(val);
      return { setValue: val, optionId: opt.optionId };
    }
    const opt = valueToValueOption(v);
    return { setValue: v, optionId: opt.optionId };
  }

  dbg(opts, "findMatchingLeftRightMultiOption: no option matched", {
    attrName,
    side,
    selectedValues,
  });
  return null;
}

/**
 * {@link findMatchingLeftRightMultiOption}, falling back to whichever option is tagged
 * `metadata.cpq_value` (or has raw value) equal to `CPQ_VALUE_FALLBACK` ("None") when nothing is
 * eligible for `side` — same fallback behavior as {@link resolveValueForAttribute}.
 */
function resolveLeftRightMultiValueForAttribute(
  attr: Record<string, unknown>,
  side: LeftRightSide,
  selectedValues: (string | number)[],
  selections: Record<string, string | number>,
  opts: ApplyCPQSelectionsOptions
): { setValue: unknown; optionId: string; viaFallback?: boolean } | null {
  const attrName = typeof attr.name === "string" ? attr.name : "?";
  const direct = findMatchingLeftRightMultiOption(attr, side, selectedValues, selections, opts);
  if (direct) {
    dbg(opts, "resolveLeftRightMultiValueForAttribute: resolved directly", { attrName, side, direct });
    return direct;
  }

  dbg(opts, "resolveLeftRightMultiValueForAttribute: no direct match, trying fallback", {
    attrName,
    side,
    fallbackValue: CPQ_VALUE_FALLBACK,
  });
  const fallback = findMatchingOption(attr, CPQ_VALUE_FALLBACK, opts);
  if (fallback) {
    dbg(opts, "resolveLeftRightMultiValueForAttribute: resolved via fallback", {
      attrName,
      side,
      fallback,
    });
    return { ...fallback, viaFallback: true };
  }

  dbg(opts, "resolveLeftRightMultiValueForAttribute: no match at all (not even fallback)", {
    attrName,
    side,
  });
  return null;
}

/** Value used to look up a fallback option when no option matches the actual CPQ value. */
const CPQ_VALUE_FALLBACK = "None";

/** Label used to identify an option in debug logs (works for both asset and raw-value options). */
function describeOption(v: unknown): unknown {
  if (isAssetValue(v)) return { assetId: v.assetId, name: v.name ?? v.label };
  if (isRecord(v) && "value" in v) return { value: v.value };
  return v;
}

/**
 * Searches an attribute's option list for one matching `targetValue`, via
 * `metadata.cpq_value` first, then (for non-item attributes) the option's raw value.
 */
function findMatchingOption(
  attr: Record<string, unknown>,
  targetValue: string | number,
  opts: ApplyCPQSelectionsOptions
): { setValue: unknown; optionId: string } | null {
  const attrName = typeof attr.name === "string" ? attr.name : "?";
  const typeStr = typeof attr.type === "string" ? attr.type : "";
  const rawValues = attr.values;
  if (!Array.isArray(rawValues)) {
    dbg(opts, "findMatchingOption: attr.values is not an array", {
      attrName,
      values: attr.values,
    });
    return null;
  }

  const attrMeta = getMetadata(attr);
  const removeLeftRightRaw = attrMeta?.removeLeftRight;
  const removeLeftRightOn = isMetadataFlagTrue(removeLeftRightRaw);
  dbg(opts, "findMatchingOption: start", {
    attrName,
    type: typeStr,
    targetValue,
    "metadata.removeLeftRight": removeLeftRightRaw,
    removeLeftRightOn,
  });

  const resolvedTarget: string | number =
    typeof targetValue === "string" && removeLeftRightOn
      ? normalizeRemoveLeftRightValue(targetValue)
      : targetValue;

  if (typeof targetValue === "string" && removeLeftRightOn) {
    dbg(opts, "findMatchingOption: removeLeftRight normalization applied", {
      attrName,
      before: targetValue,
      after: resolvedTarget,
    });
  } else if (removeLeftRightOn) {
    dbg(
      opts,
      "findMatchingOption: removeLeftRight is on but targetValue isn't a string, skipping normalization",
      { attrName, targetValue }
    );
  }

  if (isItemLikeAttributeType(typeStr)) {
    for (const v of rawValues) {
      if (!isAssetValue(v)) {
        dbg(opts, "findMatchingOption: option is not an asset value, skipping", {
          attrName,
          option: v,
        });
        continue;
      }
      const meta = getMetadata(v);
      const cpqValue = meta?.cpq_value;
      const isMatch =
        !!meta && "cpq_value" in meta && cpqValuesMatch(resolvedTarget, cpqValue);
      dbg(opts, "findMatchingOption: checking asset option", {
        attrName,
        option: describeOption(v),
        "option.metadata.cpq_value": cpqValue,
        resolvedTarget,
        isMatch,
      });
      if (isMatch) {
        dbg(opts, "findMatchingOption: matched", {
          attrName,
          option: describeOption(v),
        });
        return { setValue: { assetId: v.assetId }, optionId: v.assetId };
      }
    }
    dbg(opts, "findMatchingOption: no asset option matched", {
      attrName,
      resolvedTarget,
    });
    return null;
  }

  for (const v of rawValues) {
    if (isRecord(v)) {
      const meta = getMetadata(v);
      const cpqValue = meta?.cpq_value;
      const isMatch =
        !!meta && "cpq_value" in meta && cpqValuesMatch(resolvedTarget, cpqValue);
      dbg(opts, "findMatchingOption: checking record option (metadata.cpq_value)", {
        attrName,
        option: describeOption(v),
        "option.metadata.cpq_value": cpqValue,
        resolvedTarget,
        isMatch,
      });
      if (isMatch) {
        dbg(opts, "findMatchingOption: matched", {
          attrName,
          option: describeOption(v),
        });
        if (isAssetValue(v)) {
          return { setValue: { assetId: v.assetId }, optionId: v.assetId };
        }
        if ("value" in v) {
          const val = v.value;
          const opt = valueToValueOption(val);
          return { setValue: val, optionId: opt.optionId };
        }
        const opt = valueToValueOption(v);
        return { setValue: v, optionId: opt.optionId };
      }
    }
    const rawMatch = cpqValuesMatch(resolvedTarget, v);
    dbg(opts, "findMatchingOption: checking raw option value", {
      attrName,
      option: v,
      resolvedTarget,
      isMatch: rawMatch,
    });
    if (rawMatch) {
      dbg(opts, "findMatchingOption: matched (raw value)", { attrName, option: v });
      const opt = valueToValueOption(v);
      return { setValue: v, optionId: opt.optionId };
    }
  }

  dbg(opts, "findMatchingOption: no option matched", { attrName, resolvedTarget });
  return null;
}

/**
 * Resolves a CPQ value to a Threekit option on `attr`. If no option matches the
 * actual CPQ value, falls back to whichever option is tagged `metadata.cpq_value`
 * (or has raw value) equal to `CPQ_VALUE_FALLBACK` ("None"), instead of skipping.
 */
function resolveValueForAttribute(
  attr: Record<string, unknown>,
  cpqValue: string | number,
  opts: ApplyCPQSelectionsOptions
): { setValue: unknown; optionId: string; viaFallback?: boolean } | null {
  const attrName = typeof attr.name === "string" ? attr.name : "?";
  const direct = findMatchingOption(attr, cpqValue, opts);
  if (direct) {
    dbg(opts, "resolveValueForAttribute: resolved directly", { attrName, direct });
    return direct;
  }

  dbg(
    opts,
    "resolveValueForAttribute: no direct match, trying fallback",
    { attrName, fallbackValue: CPQ_VALUE_FALLBACK }
  );
  const fallback = findMatchingOption(attr, CPQ_VALUE_FALLBACK, opts);
  if (fallback) {
    dbg(opts, "resolveValueForAttribute: resolved via fallback", {
      attrName,
      fallback,
    });
    return { ...fallback, viaFallback: true };
  }

  dbg(opts, "resolveValueForAttribute: no match at all (not even fallback)", {
    attrName,
  });
  return null;
}

/**
 * True for a CPQ multi-select field that maps to a single Threekit attribute by matching each
 * comma-separated CPQ selection against *option-level* `metadata.cpq_value` (the normal
 * single-select convention) — as opposed to the attribute-level `metadata.cpq_value` boolean-
 * per-value convention handled by {@link resolveMultiselectBooleanValue}. Both are tagged
 * `metadata.multiselect: true` on the attribute; the discriminator is whether the attribute
 * *itself* also carries `metadata.cpq_value` (boolean case) or not (this case, where `cpq_value`
 * lives only on the values).
 */
function isMultiselectSingleSelectAttribute(attr: Record<string, unknown>): boolean {
  const attrMeta = getMetadata(attr);
  if (!attrMeta) return false;
  if (!isMetadataFlagTrue(attrMeta.multiselect)) return false;

  const cpqValueMeta = attrMeta.cpq_value;
  const hasCpqValueMeta =
    (typeof cpqValueMeta === "string" || typeof cpqValueMeta === "number") &&
    String(cpqValueMeta).trim() !== "";

  return !hasCpqValueMeta;
}

/**
 * Resolves a `metadata.multiselect` attribute whose options (not the attribute itself) carry
 * `metadata.cpq_value` — see {@link isMultiselectSingleSelectAttribute}. The raw CPQ value is
 * comma-delimited (same convention as `removeLeftRight`/`LeftRightMulti` — see
 * `splitCommaDelimitedValues` — not the "~" `takeFirstTildeDelimitedValue` uses for the
 * unrelated generic multi-select-to-single-select case). Since Threekit can only hold one
 * selected option, each comma-separated selection is checked in order against the option list
 * via {@link findMatchingOption}, and the *first selection that actually matches an option*
 * wins — not simply the first selected value regardless of match.
 */
function findMatchingMultiselectOption(
  attr: Record<string, unknown>,
  rawValue: string | number,
  opts: ApplyCPQSelectionsOptions
): { setValue: unknown; optionId: string } | null {
  const attrName = typeof attr.name === "string" ? attr.name : "?";
  const selectedValues =
    typeof rawValue === "string" ? splitCommaDelimitedValues(rawValue) : [rawValue];

  dbg(opts, "findMatchingMultiselectOption: start", { attrName, rawValue, selectedValues });

  for (const val of selectedValues) {
    const match = findMatchingOption(attr, val, opts);
    if (match) {
      dbg(opts, "findMatchingMultiselectOption: matched", {
        attrName,
        matchedValue: val,
        match,
      });
      return match;
    }
  }

  dbg(opts, "findMatchingMultiselectOption: no selected value matched any option", {
    attrName,
    selectedValues,
  });
  return null;
}

/**
 * {@link findMatchingMultiselectOption}, falling back to whichever option is tagged
 * `metadata.cpq_value` (or has raw value) equal to `CPQ_VALUE_FALLBACK` ("None") when none of
 * the comma-separated selections match — same fallback behavior as
 * {@link resolveValueForAttribute} and {@link resolveLeftRightMultiValueForAttribute}.
 */
function resolveMultiselectValueForAttribute(
  attr: Record<string, unknown>,
  rawValue: string | number,
  opts: ApplyCPQSelectionsOptions
): { setValue: unknown; optionId: string; viaFallback?: boolean } | null {
  const attrName = typeof attr.name === "string" ? attr.name : "?";
  const direct = findMatchingMultiselectOption(attr, rawValue, opts);
  if (direct) {
    dbg(opts, "resolveMultiselectValueForAttribute: resolved directly", { attrName, direct });
    return direct;
  }

  dbg(opts, "resolveMultiselectValueForAttribute: no direct match, trying fallback", {
    attrName,
    fallbackValue: CPQ_VALUE_FALLBACK,
  });
  const fallback = findMatchingOption(attr, CPQ_VALUE_FALLBACK, opts);
  if (fallback) {
    dbg(opts, "resolveMultiselectValueForAttribute: resolved via fallback", {
      attrName,
      fallback,
    });
    return { ...fallback, viaFallback: true };
  }

  dbg(opts, "resolveMultiselectValueForAttribute: no match at all (not even fallback)", {
    attrName,
  });
  return null;
}

interface ResolvedRow {
  cpqKey: string;
  attr: Record<string, unknown>;
  attrName: string;
  setValue: unknown;
  optionId: string;
  /** True for `metadata.multiselect` Boolean attributes — skip optionsShow/optionInteraction. */
  isBooleanToggle?: boolean;
}

/**
 * Maps CPQ keys/values via `metadata.cpq_attribute` (or `metadata.cpq_attributeAlt`, for
 * attributes that map to a different CPQ field depending on which model is selected) /
 * `metadata.cpq_value` (or, for `Boolean` attributes, `metadata.cpq_true_value` — see
 * {@link resolveScalarBooleanValue}), applies `setConfiguration`, then runs buyer analytics.
 * Intended to be scheduled from the synchronous `handle.applyCPQSelections` entry point.
 *
 * When two Threekit attributes share the same `cpq_attribute` and are tagged
 * `metadata.LeftRightMulti` ("Left"/"Right"), each is resolved independently via
 * {@link resolveLeftRightMultiValueForAttribute} instead of the single-value path above — see
 * that function and {@link findMatchingLeftRightMultiOption} for the `cpq_leftright` /
 * `leftRightQtyAttr` value-level metadata this depends on.
 */
export async function runApplyCPQSelections(
  handle: ApplyCPQSelectionsHandle,
  selections: Record<string, string | number>,
  options: ApplyCPQSelectionsOptions = {}
): Promise<ApplyCPQSelectionsResult> {
  const useDisplay = options.useDisplayAttributes === true;
  dbg(options, "start", {
    selectionKeys: Object.keys(selections),
    useDisplayAttributes: useDisplay,
    emitOptionsShowBeforeInteraction: options.emitOptionsShowBeforeInteraction !== false,
    productAssetId: handle.productAssetId,
  });

  const attrs = getConfiguratorAttributeList(handle.configurator, useDisplay);
  dbg(options, "attributes loaded", { count: attrs.length });

  const skipped: ApplyCPQSelectionsResult["skipped"] = [];
  const resolved: ResolvedRow[] = [];

  const attrByCpq = new Map<string, Record<string, unknown>[]>();
  for (const raw of attrs) {
    if (!isRecord(raw)) continue;
    // Index under both `cpq_attribute` and `cpq_attributeAlt` — the same Threekit attribute can
    // be reached via either CPQ field name (whichever one actually exists depends on the model
    // currently selected), and only one of the two will be present in `selections` at a time.
    const ck = getCpqAttributeKeyFromDisplayAttribute(raw);
    const ckAlt = getCpqAttributeAltKeyFromDisplayAttribute(raw);
    for (const key of [ck, ckAlt]) {
      if (!key) continue;
      const list = attrByCpq.get(key) ?? [];
      list.push(raw);
      attrByCpq.set(key, list);
    }
  }

  dbg(options, "cpq_attribute index", {
    keysFound: [...attrByCpq.keys()],
    entries: [...attrByCpq.entries()].map(([k, list]) => ({
      cpq_attribute: k,
      threekitNames: list.map((a) =>
        typeof a.name === "string" ? a.name : "?"
      ),
    })),
  });

  for (const cpqKey of Object.keys(selections)) {
    const normalizedKey = normalizeCpqKey(cpqKey);
    const matches = attrByCpq.get(normalizedKey) ?? [];
    const rawValue = selections[cpqKey]!;

    dbg(options, "resolve key", {
      cpqKey,
      normalizedKey,
      rawValue,
      matchCount: matches.length,
    });

    if (matches.length === 0) {
      skipped.push({
        cpqKey,
        reason:
          "No Threekit attribute with metadata.cpq_attribute or metadata.cpq_attributeAlt matching this key",
      });
      dbg(options, "skip", cpqKey, skipped[skipped.length - 1]!.reason);
      continue;
    }
    if (matches.length > 1) {
      dbg(options, "multiple attributes share cpq_attribute — applying to all", {
        cpqKey,
        normalizedKey,
        threekitNames: matches.map((a) => (typeof a.name === "string" ? a.name : "?")),
      });
    }

    for (const attr of matches) {
      const attrName = typeof attr.name === "string" ? attr.name : "";
      if (!attrName) {
        skipped.push({ cpqKey, reason: "Matched attribute has no name" });
        dbg(options, "skip", cpqKey, skipped[skipped.length - 1]!.reason);
        continue;
      }

      const leftRightSide = getLeftRightMultiSide(attr);
      if (leftRightSide) {
        // `LeftRightMulti` raw CPQ values are comma-delimited (like `removeLeftRight`, not the
        // "~" convention `takeFirstTildeDelimitedValue` handles), and every selected value is
        // needed here, not just the first — see `splitCommaDelimitedValues`.
        const selectedValues: (string | number)[] =
          typeof rawValue === "string" ? splitCommaDelimitedValues(rawValue) : [rawValue];
        dbg(options, "LeftRightMulti attribute", { cpqKey, attrName, side: leftRightSide, selectedValues });

        const resolvedVal = resolveLeftRightMultiValueForAttribute(
          attr,
          leftRightSide,
          selectedValues,
          selections,
          options
        );
        if (!resolvedVal) {
          skipped.push({
            cpqKey,
            reason: `No option value matched for LeftRightMulti Threekit attribute "${attrName}" (side "${leftRightSide}", cpq_value metadata, cpq_leftright, leftRightQtyAttr, or "${CPQ_VALUE_FALLBACK}" fallback)`,
          });
          dbg(options, "skip", cpqKey, skipped[skipped.length - 1]!.reason, {
            threekitAttributeName: attrName,
            side: leftRightSide,
            selectedValues,
          });
          continue;
        }

        dbg(options, "resolved (LeftRightMulti)", {
          cpqKey,
          threekitAttributeName: attrName,
          side: leftRightSide,
          optionId: resolvedVal.optionId,
          setValue: resolvedVal.setValue,
          viaFallback: resolvedVal.viaFallback === true,
        });

        resolved.push({
          cpqKey,
          attr,
          attrName,
          setValue: resolvedVal.setValue,
          optionId: resolvedVal.optionId,
        });
        continue;
      }

      if (isMultiselectSingleSelectAttribute(attr)) {
        dbg(options, "multiselect single-select attribute", { cpqKey, attrName, rawValue });

        const resolvedVal = resolveMultiselectValueForAttribute(attr, rawValue, options);
        if (!resolvedVal) {
          skipped.push({
            cpqKey,
            reason: `No option value matched for multiselect Threekit attribute "${attrName}" (option-level cpq_value metadata, or "${CPQ_VALUE_FALLBACK}" fallback)`,
          });
          dbg(options, "skip", cpqKey, skipped[skipped.length - 1]!.reason, {
            threekitAttributeName: attrName,
            rawValue,
          });
          continue;
        }

        dbg(options, "resolved (multiselect single-select)", {
          cpqKey,
          threekitAttributeName: attrName,
          optionId: resolvedVal.optionId,
          setValue: resolvedVal.setValue,
          viaFallback: resolvedVal.viaFallback === true,
        });

        resolved.push({
          cpqKey,
          attr,
          attrName,
          setValue: resolvedVal.setValue,
          optionId: resolvedVal.optionId,
        });
        continue;
      }

      const cpqValue = takeFirstTildeDelimitedValue(rawValue, options);

      const multiselectBoolean = resolveMultiselectBooleanValue(attr, cpqValue, options);
      if (multiselectBoolean !== null) {
        dbg(options, "resolved via multiselect boolean metadata", {
          cpqKey,
          attrName,
          setValue: multiselectBoolean,
        });
        resolved.push({
          cpqKey,
          attr,
          attrName,
          setValue: multiselectBoolean,
          optionId: String(multiselectBoolean),
          isBooleanToggle: true,
        });
        continue;
      }

      const scalarBoolean = resolveScalarBooleanValue(attr, cpqValue, options);
      if (scalarBoolean !== null) {
        dbg(options, "resolved via scalar boolean metadata (cpq_true_value)", {
          cpqKey,
          attrName,
          setValue: scalarBoolean,
        });
        resolved.push({
          cpqKey,
          attr,
          attrName,
          setValue: scalarBoolean,
          optionId: String(scalarBoolean),
          isBooleanToggle: true,
        });
        continue;
      }

      const isEmptyValue = typeof cpqValue === "string" && cpqValue.trim() === "";
      dbg(options, "empty-value check", {
        cpqKey,
        attrName,
        isEmptyValue,
      });
      if (isEmptyValue) {
        dbg(
          options,
          "empty CPQ value — treating as cpq_value = \"None\" and resolving to the \"None\" fallback",
          { cpqKey, attrName }
        );
      }

      const resolvedVal = resolveValueForAttribute(attr, cpqValue, options);
      if (!resolvedVal) {
        skipped.push({
          cpqKey,
          reason: `No option value matched for Threekit attribute "${attrName}" (cpq_value metadata, raw value, or "${CPQ_VALUE_FALLBACK}" fallback)`,
        });
        dbg(options, "skip", cpqKey, skipped[skipped.length - 1]!.reason, {
          threekitAttributeName: attrName,
          type: attr.type,
          valuesSample: Array.isArray(attr.values)
            ? attr.values.slice(0, 5)
            : attr.values,
        });
        continue;
      }

      dbg(options, "resolved", {
        cpqKey,
        threekitAttributeName: attrName,
        optionId: resolvedVal.optionId,
        setValue: resolvedVal.setValue,
        viaFallback: resolvedVal.viaFallback === true,
      });

      resolved.push({
        cpqKey,
        attr,
        attrName,
        setValue: resolvedVal.setValue,
        optionId: resolvedVal.optionId,
      });
    }
  }

  const configuration: Record<string, unknown> = {};
  for (const row of resolved) {
    configuration[row.attrName] = row.setValue;
  }

  const configKeys = Object.keys(configuration);
  if (configKeys.length > 0) {
    dbg(options, "setConfiguration", configuration);
    try {
      await handle.setConfiguration(configuration);
      dbg(options, "setConfiguration done");
    } catch (e) {
      dbg(options, "setConfiguration threw", e);
      throw e;
    }
  } else {
    dbg(options, "setConfiguration skipped (empty configuration)");
  }

  const emitShow = options.emitOptionsShowBeforeInteraction !== false;
  const parentAssetId = handle.productAssetId;

  for (const row of resolved) {
    if (row.isBooleanToggle) {
      dbg(options, "boolean toggle — skipping optionsShow/optionInteraction", {
        attrName: row.attrName,
        setValue: row.setValue,
      });
      continue;
    }
    if (emitShow) {
      const showParams = buildOptionsShowForAttribute(row.attr, parentAssetId);
      dbg(options, "optionsShow", row.attrName, showParams ?? "(no params)");
      if (showParams) handle.optionsShow(showParams);
    }
    const interaction = {
      optionsSetId: row.attrName,
      interactionType: OptionInteractionType.Select,
      optionId: row.optionId,
      ...(parentAssetId ? { assetId: parentAssetId } : {}),
    } as OptionInteractionParams;
    dbg(options, "optionInteraction", interaction);
    handle.optionInteraction(interaction);
  }

  const result = {
    applied: [...new Set(resolved.map((r) => r.cpqKey))],
    skipped,
  };
  dbg(options, "finished", result);
  return result;
}

/**
 * Starts async CPQ apply; returns immediately. Use `onComplete` for outcomes.
 */
export function scheduleApplyCPQSelections(
  handle: ApplyCPQSelectionsHandle,
  selections: Record<string, string | number>,
  options?: ApplyCPQSelectionsOptions
): void {
  const opts = options ?? {};
  dbg(opts, "scheduled (sync return; work runs async)");
  void runApplyCPQSelections(handle, selections, opts)
    .then((result) => {
      opts.onComplete?.(null, result);
    })
    .catch((err) => {
      const error = err instanceof Error ? err : new Error(String(err));
      if (opts.debug) {
        console.error(LOG_PREFIX, "async error", error);
      }
      if (opts.onComplete) {
        opts.onComplete(error, null);
      } else {
        console.error(LOG_PREFIX, "failed (no onComplete):", error);
      }
    });
}
